import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { Helmet } from 'react-helmet';
import { auth } from '../firebase.ts';
import AdminLogin from './AdminLogin.tsx';
import AdminDashboard from './AdminDashboard.tsx';
// RichTextEditor removed - now image-only
import MenuAuditTable from './MenuAuditTable.tsx';
import { loadStoreProducts } from '../services/AdminMenuService.ts';
import { MenuProduct } from '../services/MenuService.ts';
// ContentTypeSelector removed - now image-only
import ImageUploadManager from './ImageUploadManager.tsx';
import StoreHoursSelector from './StoreHoursSelector.tsx';
import StoreHoursEditor from './StoreHoursEditor.tsx';
import TemporaryClosuresManager from './TemporaryClosuresManager.tsx';
import StoreChangesConfirmation from './StoreChangesConfirmation.tsx';
import ScheduledDealsQueue from './ScheduledDealsQueue.tsx';
import DeepLinkConfigurator from './DeepLinkConfigurator.tsx';
import RegulatoryLinkInput from './RegulatoryLinkInput.tsx';
import CartMessageEditor from './CartMessageEditor.tsx';
import HomeCollectionsEditor from './HomeCollectionsEditor.tsx';
import { getDealMemo, saveDealMemo, getAllStoreDeals, deleteScheduledDeal, reorderDeals, createScheduledImageDeal, updateScheduledImageDeal, pauseScheduledDeal, DealMemoData, StoreDealsData, ScheduledDealData } from '../services/dealsApiService.ts';
import { DeepLinkConfig, generateDeepLinkUrl, Environment } from '../services/DeepLinkService.ts';
import { MENU_ID_TO_STORE, STORE_MENU_CONFIG } from '../config/storeMenuConfig.ts';
import { 
  getStoreHoursForAdmin, 
  saveStoreHours, 
  updateTemporaryClosures,
  addHolidaysToStore,
  StoreHoursData, 
  WeeklyHours, 
  TemporaryClosure 
} from '../services/storeHoursApiService.ts';
import {
  getDateInputValue,
  getTimeInputValue,
  combineDateTimeInputs,
  getTimezoneAbbreviation,
  isBrowserTimzoneDifferent,
  convertUtcToStoreTimezone,
  isDateInPast,
  logTimezoneDebug
} from '../utils/timezoneUtils.ts';
import './AdminPortal.css';

const AdminPortal: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'selector' | 'deals' | 'deals-schedule' | 'store-hours' | 'store-hours-edit' | 'menu-audit' | 'cart-message' | 'home-collections'>('selector');
  const [selectedDoc, setSelectedDoc] = useState<{id: string, name: string} | null>(null);
  const [selectedStore, setSelectedStore] = useState<{id: string, name: string} | null>(null);
  const [dealData, setDealData] = useState<DealMemoData | null>(null);
  const [storeDealsData, setStoreDealsData] = useState<StoreDealsData | null>(null);
  const [storeHoursData, setStoreHoursData] = useState<StoreHoursData | null>(null);
  // content state removed - text deals no longer supported
  // Deal type is always 'image' - text deals removed
  const dealType = 'image' as const;
  const [mobileImageUrl, setMobileImageUrl] = useState<string | null>(null);
  const [desktopImageUrl, setDesktopImageUrl] = useState<string | null>(null);
  const [mobileImageFile, setMobileImageFile] = useState<File | null>(null);
  const [desktopImageFile, setDesktopImageFile] = useState<File | null>(null);

  // Banner link type selector: 'none' | 'filter' | 'product' | 'special' | 'regulatory'
  const [bannerLinkType, setBannerLinkType] = useState<'none' | 'filter' | 'product' | 'special' | 'regulatory'>('none');

  // Deep link state
  const [deepLinkConfig, setDeepLinkConfig] = useState<DeepLinkConfig | null>(null);

  // Regulatory link state (.gov URLs only)
  const [regulatoryLink, setRegulatoryLink] = useState<string | null>(null);

  // Scheduling state
  const [schedulingEnabled, setSchedulingEnabled] = useState(false);
  const [startDateTime, setStartDateTime] = useState<Date | null>(null);
  const [endDateTime, setEndDateTime] = useState<Date | null>(null);
  const [viewingScheduledDeal, setViewingScheduledDeal] = useState<ScheduledDealData | null>(null);
  const [isEditing, setIsEditing] = useState(false); // Toggle between view and edit mode for scheduled deals
  const [targetMenuTypes, setTargetMenuTypes] = useState<('medical' | 'recreational')[]>(['medical', 'recreational']); // Publish to Med/Rec/Both

  // Recurring schedule state
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringDays, setRecurringDays] = useState<number[]>([]); // 0=Sun, 1=Mon, etc.
  const [recurringWeeks, setRecurringWeeks] = useState(4); // Number of weeks to generate

  // Helper to get paired menu IDs for a store (medical and recreational)
  const getPairedMenuIds = (legacyMenuId: string): { medical?: string; recreational?: string; storePath?: string } => {
    const storeInfo = MENU_ID_TO_STORE[legacyMenuId];
    if (!storeInfo) return {};

    const storePath = `${storeInfo.state}/${storeInfo.store}`;
    const storeConfig = STORE_MENU_CONFIG[storePath];
    if (!storeConfig) return {};

    return {
      medical: storeConfig.medical?.legacyMenuId,
      recreational: storeConfig.recreational?.legacyMenuId,
      storePath
    };
  };

  // Separate date and time state for better UX
  const [startDate, setStartDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [startAmPm, setStartAmPm] = useState<'AM' | 'PM'>('AM');
  const [endDate, setEndDate] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [endAmPm, setEndAmPm] = useState<'AM' | 'PM'>('PM');
  const [hours, setHours] = useState<WeeklyHours | null>(null);
  const [originalHours, setOriginalHours] = useState<WeeklyHours | null>(null);
  const [temporaryClosures, setTemporaryClosures] = useState<TemporaryClosure[]>([]);
  const [originalTemporaryClosures, setOriginalTemporaryClosures] = useState<TemporaryClosure[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);

  // Menu Audit state
  const [menuAuditProducts, setMenuAuditProducts] = useState<MenuProduct[]>([]);
  const [selectedMenu, setSelectedMenu] = useState<{retailerId: string, legacyMenuId: string, storeName: string, menuType: string} | null>(null);

  // Home Collections state
  const [homeCollectionsContext, setHomeCollectionsContext] = useState<{storeName: string, menuType: string, displayName: string, retailerId: string, legacyMenuId: string} | null>(null);

  // Helper functions for date/time conversion using store timezone
  const combineDateTime = (date: string, time: string, ampm: 'AM' | 'PM', storeId: string): Date | null => {
    if (!date || !time) return null;
    
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    
    let hour24 = hours;
    if (ampm === 'PM' && hours !== 12) hour24 += 12;
    if (ampm === 'AM' && hours === 12) hour24 = 0;
    
    // Create time string in 24-hour format
    const timeStr = `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    
    // Use timezone-aware combination
    return combineDateTimeInputs(date, timeStr, storeId);
  };

  const formatTimeFromDate = (date: Date): { time: string; ampm: 'AM' | 'PM' } => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    if (hours > 12) hours -= 12;
    if (hours === 0) hours = 12;
    
    return {
      time: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
      ampm
    };
  };

  // Generate recurring schedule dates based on selected days and weeks
  const generateRecurringDates = (
    baseStartTime: string,
    baseStartAmPm: 'AM' | 'PM',
    baseEndTime: string,
    baseEndAmPm: 'AM' | 'PM',
    days: number[],
    weeks: number,
    storeId: string
  ): Array<{ startDateTime: Date; endDateTime: Date }> => {
    const dates: Array<{ startDateTime: Date; endDateTime: Date }> = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // For each week
    for (let week = 0; week < weeks; week++) {
      // For each selected day
      for (const dayOfWeek of days) {
        // Calculate the date for this day in this week
        const date = new Date(today);
        const currentDay = date.getDay();
        let daysToAdd = dayOfWeek - currentDay;
        if (daysToAdd < 0 || (daysToAdd === 0 && week === 0)) {
          daysToAdd += 7; // Move to next week if day has passed
        }
        daysToAdd += week * 7;
        date.setDate(date.getDate() + daysToAdd);

        // Format date as YYYY-MM-DD
        const dateStr = date.toISOString().split('T')[0];

        // Create start and end times using the same logic as combineDateTime
        const startDt = combineDateTime(dateStr, baseStartTime, baseStartAmPm, storeId);
        const endDt = combineDateTime(dateStr, baseEndTime, baseEndAmPm, storeId);

        if (startDt && endDt) {
          dates.push({ startDateTime: startDt, endDateTime: endDt });
        }
      }
    }

    // Sort by start date
    dates.sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());
    return dates;
  };

  // Get store context from legacy menu ID (for deep link configurator)
  const getStoreContext = (legacyMenuId: string) => {
    const storeInfo = MENU_ID_TO_STORE[legacyMenuId];
    if (!storeInfo) {
      console.error('[AdminPortal] No store info found for legacy menu ID:', legacyMenuId);
      return null;
    }

    const storeConfigKey = `${storeInfo.state}/${storeInfo.store}`;
    const storeConfig = STORE_MENU_CONFIG[storeConfigKey];
    if (!storeConfig) {
      console.error('[AdminPortal] No store config found for:', storeConfigKey);
      return null;
    }

    const menuConfig = storeConfig[storeInfo.type];
    if (!menuConfig) {
      console.error('[AdminPortal] No menu config found for type:', storeInfo.type);
      return null;
    }

    return {
      storeId: storeConfigKey,
      menuType: storeInfo.type,
      retailerId: menuConfig.menuId,
      legacyMenuId: legacyMenuId
    };
  };

  // Update combined date/time when individual inputs change (using store timezone)
  useEffect(() => {
    if (selectedDoc) {
      const newStartDateTime = combineDateTime(startDate, startTime, startAmPm, selectedDoc.id);
      setStartDateTime(newStartDateTime);
    }
  }, [startDate, startTime, startAmPm, selectedDoc]);

  useEffect(() => {
    if (selectedDoc) {
      const newEndDateTime = combineDateTime(endDate, endTime, endAmPm, selectedDoc.id);
      setEndDateTime(newEndDateTime);
    }
  }, [endDate, endTime, endAmPm, selectedDoc]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setMode('selector');
      setSelectedDoc(null);
      setSelectedStore(null);
      setDealData(null);
      setStoreHoursData(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleDocumentSelect = async (docId: string, displayName: string) => {
    setLoadingDoc(true);
    try {
      // In test mode, skip API calls and use empty/mock data
      if (isTestMode) {
        setSelectedDoc({id: docId, name: displayName});
        setStoreDealsData({
          storeId: docId,
          storeName: displayName,
          currentDeals: [],
          scheduledDeals: []
        });
        setDealData(null);
        // dealType is always 'image'
        setMobileImageUrl(null);
        setDesktopImageUrl(null);
        setMode('deals');
        return;
      }

      // Load all deals for this store (current + scheduled)
      const allDeals = await getAllStoreDeals(docId);

      setSelectedDoc({id: docId, name: displayName});
      setStoreDealsData(allDeals);

      // Don't auto-populate dealData - only set when user explicitly edits
      // This prevents the editor from showing by default
      setDealData(null);

      // Set default editor state (but don't show editor)
      // dealType is always 'image'
      setMobileImageUrl(null);
      setDesktopImageUrl(null);

      setMode('deals');
    } catch (error) {
      console.error('Error loading store deals:', error);
      alert(`Failed to load store deals: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleStoreHoursSelect = (storeId: string, storeName: string) => {
    handleStoreSelect(storeId, storeName);
  };

  const handleStoreSelect = async (storeId: string, storeName: string) => {
    setLoadingDoc(true);
    try {
      const data = await getStoreHoursForAdmin(storeId);
      setSelectedStore({id: storeId, name: storeName});
      setStoreHoursData(data);
      setHours(data?.hours || null);
      setOriginalHours(data?.hours || null);
      setTemporaryClosures(data?.temporaryClosures || []);
      setOriginalTemporaryClosures(data?.temporaryClosures || []);
      setMode('store-hours-edit');
    } catch (error) {
      console.error('Error loading store hours:', error);
      alert(`Failed to load store hours: ${error.message}`);
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleMenuAuditSelect = async (retailerId: string, legacyMenuId: string, storeName: string, menuType: string) => {
    setLoadingDoc(true);
    try {
      const products = await loadStoreProducts(retailerId);
      setSelectedMenu({retailerId, legacyMenuId, storeName, menuType});
      setMenuAuditProducts(products);
      setMode('menu-audit');
      // Scroll to top when menu audit loads
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error loading menu products:', error);
      alert(`Failed to load menu products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleSave = async (publish: boolean = false) => {
    if (!selectedDoc || !user) return;
    
    
    setSaving(true);
    try {
      // Prepare deal data (always image type)
      const dealData: any = {
        dealType,
        imageUrls: { mobile: mobileImageUrl!, desktop: desktopImageUrl! },
      };
      
      // Save with new backend API
      await saveDealMemo(
        selectedDoc.id,
        dealData,
        publish
      );
      
      // Refresh the document data to get updated metadata
      const updatedData = await getDealMemo(selectedDoc.id);
      setDealData(updatedData);
      
      alert(publish ? 'Deals published successfully!' : 'Deals saved successfully!');
    } catch (error) {
      console.error('Error saving document:', error);
      alert(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHours = () => {
    if (!originalHours || !hours) return;
    
    // Check if there are any changes to save
    const hoursChanged = JSON.stringify(originalHours) !== JSON.stringify(hours);
    const closuresChanged = JSON.stringify(originalTemporaryClosures) !== JSON.stringify(temporaryClosures);
    
    
    if (!hoursChanged && !closuresChanged) {
      alert('No changes to save.');
      return;
    }
    
    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    if (!selectedStore || !user || !hours) return;
    
    setSaving(true);
    setShowConfirmation(false);
    
    try {
      // Determine what changed
      const hoursChanged = JSON.stringify(originalHours) !== JSON.stringify(hours);
      const closuresChanged = JSON.stringify(temporaryClosures) !== JSON.stringify(originalTemporaryClosures);

      // Save regular hours if they changed
      if (hoursChanged) {
        await saveStoreHours(
          selectedStore.id,
          hours
        );
      }
      
      // Save temporary closures if they changed
      if (closuresChanged) {
        await updateTemporaryClosures(
          selectedStore.id,
          temporaryClosures
        );
      }
      
      // Show appropriate success message
      if (hoursChanged && closuresChanged) {
        alert('Store hours and temporary closures saved successfully!');
      } else if (hoursChanged) {
        alert('Store hours saved successfully!');
      } else if (closuresChanged) {
        alert('Temporary closures saved successfully!');
      }
      // Return to main admin menu after successful save
      setMode('selector');
    } catch (error) {
      console.error('Error saving store hours:', error);
      alert(`Failed to save hours: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Secure test mode bypass - only works on localhost with env var
  const isTestMode = process.env.REACT_APP_CYPRESS_TEST_MODE === 'true' &&
                     window.location.hostname === 'localhost';

  if (loading) {
    return (
      <div className="admin-portal-loading">
        <p>Loading...</p>
      </div>
    );
  }

  if (!user && !isTestMode) {
    return <AdminLogin onLoginSuccess={() => {}} />;
  }

  if (loadingDoc) {
    return (
      <div className="admin-portal-container">
        <div className="admin-portal-loading">
          <div className="menu-audit-loading-spinner"></div>
          <p>Loading menu data...</p>
        </div>
      </div>
    );
  }

  if (mode === 'selector') {
    return (
      <div className="admin-portal-container" data-testid="admin-portal">
        <Helmet>
          <title>Admin Portal - Private</title>
          <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
          <meta name="googlebot" content="noindex, nofollow" />
          <meta name="description" content="Private admin area - not for public access" />
        </Helmet>
        <div className="admin-portal-header">
          <h1>Admin Portal</h1>
          <div className="user-info">
            <span>Logged in as: {user?.email || (isTestMode ? 'Test User' : '')}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
        <AdminDashboard
          onSelectDocument={handleDocumentSelect}
          onSelectStoreHours={handleStoreHoursSelect}
          onSelectMenuAudit={handleMenuAuditSelect}
          onSelectCartMessage={() => setMode('cart-message')}
          onSelectHomeCollections={(storeName, menuType, displayName, retailerId, legacyMenuId) => {
            setHomeCollectionsContext({ storeName, menuType, displayName, retailerId, legacyMenuId });
            setMode('home-collections');
          }}
        />
      </div>
    );
  }

  if (mode === 'store-hours') {
    return (
      <div className="admin-portal-container" data-testid="admin-portal">
        <div className="admin-portal-header">
          <div className="header-left">
            <button onClick={() => setMode('selector')} className="back-button">
              ← Back to Admin
            </button>
            <h1>Store Hours Management</h1>
          </div>
          <div className="user-info">
            <span>Logged in as: {user?.email || (isTestMode ? 'Test User' : '')}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
        <div className="admin-portal-content">
          <div className="store-hours-list">
            <StoreHoursSelector onSelectStore={handleStoreSelect} />
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'store-hours-edit' && selectedStore && storeHoursData && hours) {
    return (
      <div className="admin-portal-container" data-testid="admin-portal">
        <div className="admin-portal-header">
          <div className="header-left">
            <button onClick={() => setMode('selector')} className="back-button">
              ← Back to Admin
            </button>
            <div className="store-editing-title">
              <h1>Editing Store Hours</h1>
              <div className="current-store">{selectedStore.name.split(',')[0].trim().toUpperCase()}</div>
            </div>
          </div>
          <div className="user-info">
            <span>Logged in as: {user?.email || (isTestMode ? 'Test User' : '')}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
        
        {storeHoursData && (
          <div className="document-metadata">
            <div className="metadata-item">
              <strong>Last Edited:</strong> {storeHoursData.lastEdited ? storeHoursData.lastEdited.toLocaleString() : 'Never'}
            </div>
            <div className="metadata-item">
              <strong>Last Author:</strong> {storeHoursData.lastAuthor || 'Unknown'}
            </div>
          </div>
        )}
        
        <div className="admin-portal-content">
          <div className="editor-section">
            <StoreHoursEditor hours={hours} onChange={setHours} />
            <TemporaryClosuresManager 
              temporaryClosures={temporaryClosures} 
              onChange={setTemporaryClosures}
              storeId={selectedStore.id}
            />
            <div className="editor-actions">
              <button 
                onClick={() => setMode('selector')} 
                disabled={saving}
                className="cancel-button"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveHours} 
                disabled={saving}
                className="save-button"
              >
                {saving ? 'Saving...' : 'Save Hours & Closures'}
              </button>
            </div>
          </div>
        </div>
        
        {showConfirmation && originalHours && hours && selectedStore && (
          <StoreChangesConfirmation
            storeName={selectedStore.name}
            oldHours={originalHours}
            newHours={hours}
            oldClosures={originalTemporaryClosures}
            newClosures={temporaryClosures}
            onConfirm={handleConfirmSave}
            onCancel={() => setShowConfirmation(false)}
          />
        )}
      </div>
    );
  }

  if (mode === 'deals' && selectedDoc && storeDealsData) {
    return (
      <div className="admin-portal-container" data-testid="admin-portal">
        <div className="admin-portal-header">
          <div className="header-left">
            <button onClick={() => setMode('selector')} className="back-button">
              ← Back to Admin
            </button>
            <h1>{selectedDoc.name} Deals</h1>
          </div>
          <div className="user-info">
          <span>Logged in as: {user?.email || (isTestMode ? 'Test User' : '')}</span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </div>
      
      
      <ScheduledDealsQueue
        scheduledDeals={storeDealsData.scheduledDeals}
        storeId={selectedDoc.id}
        onViewDeal={(deal) => {
          // View scheduled deal - open in read-only mode
          setViewingScheduledDeal(deal);
          setDealData(deal);
          // dealType is always 'image'

          if (deal.imageUrls) {
            setMobileImageUrl(deal.imageUrls.mobile);
            setDesktopImageUrl(deal.imageUrls.desktop);
            // Note: Can't populate file state from existing URLs
            setMobileImageFile(null);
            setDesktopImageFile(null);
          } else {
            setMobileImageUrl(null);
            setDesktopImageUrl(null);
            setMobileImageFile(null);
            setDesktopImageFile(null);
          }

          // Populate deep link config if present
          setDeepLinkConfig(deal.deepLink || null);

          // Populate regulatory link if present
          setRegulatoryLink(deal.regulatoryLink || null);

          // Determine banner link type based on what's present
          if (deal.regulatoryLink) {
            setBannerLinkType('regulatory');
          } else if (deal.deepLink) {
            // Map deepLink linkType to bannerLinkType
            if (deal.deepLink.linkType === 'filter') {
              setBannerLinkType('filter');
            } else if (deal.deepLink.linkType === 'special') {
              setBannerLinkType('special');
            } else if (deal.deepLink.linkType === 'product') {
              setBannerLinkType('product');
            } else {
              setBannerLinkType('none');
            }
          } else {
            setBannerLinkType('none');
          }
          
          // Populate scheduling times from the deal using STORE TIMEZONE
          if (deal.startDateTime && deal.endDateTime && selectedDoc) {
            // Convert UTC times to store timezone for display
            const startDateInStore = convertUtcToStoreTimezone(deal.startDateTime, selectedDoc.id);
            const endDateInStore = convertUtcToStoreTimezone(deal.endDateTime, selectedDoc.id);
            
            logTimezoneDebug('Editing scheduled deal', selectedDoc.id, {
              'Start UTC': deal.startDateTime,
              'Start Store': startDateInStore,
              'End UTC': deal.endDateTime,
              'End Store': endDateInStore
            });
            
            // Use the store timezone converted dates for input population
            const startDate = getDateInputValue(startDateInStore, selectedDoc.id);
            const endDate = getDateInputValue(endDateInStore, selectedDoc.id);
            const startTime = getTimeInputValue(startDateInStore, selectedDoc.id);
            const endTime = getTimeInputValue(endDateInStore, selectedDoc.id);
            
            // Parse AM/PM from the converted store timezone times
            const startHour = startDateInStore.getHours();
            const endHour = endDateInStore.getHours();
            
            setStartDate(startDate);
            setEndDate(endDate);
            setStartTime(startTime);
            setEndTime(endTime);
            setStartAmPm(startHour >= 12 ? 'PM' : 'AM');
            setEndAmPm(endHour >= 12 ? 'PM' : 'AM');
            setSchedulingEnabled(true);

            // Preserve the original UTC datetime values directly
            // This prevents round-trip conversion issues when saving without changes
            setStartDateTime(deal.startDateTime);
            setEndDateTime(deal.endDateTime);
          } else {
            // Clear scheduling if no times
            setSchedulingEnabled(false);
            setStartDate('');
            setStartTime('');
            setStartAmPm('AM');
            setEndDate('');
            setEndTime('');
            setEndAmPm('PM');
          }
        }}
        onDeleteDeal={async (deal) => {
          if (!selectedDoc) return;
          const confirmDelete = window.confirm(
            `Are you sure you want to delete this scheduled deal? This action cannot be undone.`
          );
          if (!confirmDelete) return;
          
          try {
            await deleteScheduledDeal(selectedDoc.id, deal.documentId);
            // Refresh data
            const updatedData = await getAllStoreDeals(selectedDoc.id);
            setStoreDealsData(updatedData);
            alert('Scheduled deal deleted successfully!');
          } catch (error) {
            console.error('Error deleting scheduled deal:', error);
            alert(`Failed to delete scheduled deal: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }}
        onDuplicateDeal={(deal) => {
          // Duplicate an expired deal - copy everything except schedule
          // Set schedule to start now and end 1 week from now

          // Copy images from the expired deal
          if (deal.imageUrls) {
            setMobileImageUrl(deal.imageUrls.mobile);
            setDesktopImageUrl(deal.imageUrls.desktop);
          } else {
            setMobileImageUrl(null);
            setDesktopImageUrl(null);
          }
          // Clear file state - user may need to re-upload or images will be copied from URLs
          setMobileImageFile(null);
          setDesktopImageFile(null);

          // Copy deep link config if present
          setDeepLinkConfig(deal.deepLink || null);

          // Copy regulatory link if present
          setRegulatoryLink(deal.regulatoryLink || null);

          // Determine banner link type based on what's present
          if (deal.regulatoryLink) {
            setBannerLinkType('regulatory');
          } else if (deal.deepLink) {
            if (deal.deepLink.linkType === 'filter') {
              setBannerLinkType('filter');
            } else if (deal.deepLink.linkType === 'special') {
              setBannerLinkType('special');
            } else if (deal.deepLink.linkType === 'product') {
              setBannerLinkType('product');
            } else {
              setBannerLinkType('none');
            }
          } else {
            setBannerLinkType('none');
          }

          // Set schedule to NOW → 1 week from now
          const now = new Date();
          const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

          // Convert to store timezone for display
          if (selectedDoc) {
            const startDateInStore = convertUtcToStoreTimezone(now, selectedDoc.id);
            const endDateInStore = convertUtcToStoreTimezone(oneWeekFromNow, selectedDoc.id);

            setStartDate(getDateInputValue(startDateInStore, selectedDoc.id));
            setEndDate(getDateInputValue(endDateInStore, selectedDoc.id));
            setStartTime(getTimeInputValue(startDateInStore, selectedDoc.id));
            setEndTime(getTimeInputValue(endDateInStore, selectedDoc.id));
            setStartAmPm(startDateInStore.getHours() >= 12 ? 'PM' : 'AM');
            setEndAmPm(endDateInStore.getHours() >= 12 ? 'PM' : 'AM');
          }

          setStartDateTime(now);
          setEndDateTime(oneWeekFromNow);
          setSchedulingEnabled(true);

          // IMPORTANT: Don't set viewingScheduledDeal - this is a NEW deal, not editing
          setViewingScheduledDeal(null);
          setDealData(null);
          setIsEditing(false);

          // Clear recurring state
          setRecurringEnabled(false);
          setRecurringDays([]);
          setRecurringWeeks(4);

          // Schedule auto-set to now → 1 week - user can adjust before saving
        }}
        onReorderDeals={async (dealOrder) => {
          if (!selectedDoc) return;
          try {
            await reorderDeals(selectedDoc.id, dealOrder);
            const updatedData = await getAllStoreDeals(selectedDoc.id);
            setStoreDealsData(updatedData);
          } catch (error) {
            console.error('Error reordering deals:', error);
            alert(`Failed to reorder deals: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }}
        onTogglePause={async (deal) => {
          if (!selectedDoc) return;
          const nextPaused = !deal.isPaused;
          try {
            await pauseScheduledDeal(selectedDoc.id, deal.documentId, nextPaused);
            const updatedData = await getAllStoreDeals(selectedDoc.id);
            setStoreDealsData(updatedData);
          } catch (error) {
            console.error('Error toggling pause:', error);
            alert(`Failed to ${nextPaused ? 'pause' : 'resume'} deal: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }}
        onScheduleNew={() => {
          // Create new scheduled deal (always image mode)
          setViewingScheduledDeal(null);
          setDealData(null);
          // dealType is always 'image'
          setSchedulingEnabled(true);
          setStartDateTime(null);
          setEndDateTime(null);
          // Clear separate date/time inputs
          setStartDate('');
          setStartTime('');
          setStartAmPm('AM');
          setEndDate('');
          setEndTime('');
          setEndAmPm('PM');
          // Clear image state
          setMobileImageUrl(null);
          setDesktopImageUrl(null);
          setMobileImageFile(null);
          setDesktopImageFile(null);
          // Clear deep link
          setDeepLinkConfig(null);
          // Clear regulatory link
          setRegulatoryLink(null);
          // Clear banner link type
          setBannerLinkType('none');
          // Clear recurring state
          setRecurringEnabled(false);
          setRecurringDays([]);
          setRecurringWeeks(4);
        }}
      />

      {/* Show editor when editing a deal or scheduling new */}
      {(dealData !== null || schedulingEnabled) && (
        <div className="admin-portal-content">
          <div className="editor-section">
            {viewingScheduledDeal ? (
              // Read-only view mode - image preview
              <div className="deal-preview">
                <h4>Deal Images</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                  {mobileImageUrl && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Mobile Image:</label>
                      <img src={mobileImageUrl} alt="Mobile" style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px' }} />
                    </div>
                  )}
                  {desktopImageUrl && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Desktop Image:</label>
                      <img src={desktopImageUrl} alt="Desktop" style={{ width: '100%', border: '1px solid #ddd', borderRadius: '4px' }} />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Edit mode - image upload
              <ImageUploadManager
                storeId={selectedDoc.id}
                mobileImageUrl={mobileImageUrl}
                desktopImageUrl={desktopImageUrl}
                onMobileImageChange={setMobileImageUrl}
                onDesktopImageChange={setDesktopImageUrl}
                onMobileImageFileChange={setMobileImageFile}
                onDesktopImageFileChange={setDesktopImageFile}
                schedulingMode={schedulingEnabled}
              />
            )}

            {/* Banner Link Configuration (in create/edit mode) */}
            {(!viewingScheduledDeal || isEditing) && (
              <div className="banner-link-section">
                <h4>Banner Link Configuration (Optional)</h4>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                  Choose what happens when users click the banner image
                </p>

                <div className="banner-link-type-selector">
                  <label className="banner-link-option">
                    <input
                      type="radio"
                      name="bannerLinkType"
                      value="none"
                      checked={bannerLinkType === 'none'}
                      onChange={() => {
                        setBannerLinkType('none');
                        setDeepLinkConfig(null);
                        setRegulatoryLink(null);
                      }}
                      disabled={saving}
                    />
                    <span>No Link</span>
                  </label>

                  {selectedDoc && getStoreContext(selectedDoc.id) && (
                    <>
                      <label className="banner-link-option">
                        <input
                          type="radio"
                          name="bannerLinkType"
                          value="filter"
                          checked={bannerLinkType === 'filter'}
                          onChange={() => {
                            setBannerLinkType('filter');
                            setRegulatoryLink(null);
                            setDeepLinkConfig({ linkType: 'filter', categories: [], brands: [] });
                          }}
                          disabled={saving}
                        />
                        <span>Filter Link (Category/Brand)</span>
                      </label>

                      <label className="banner-link-option">
                        <input
                          type="radio"
                          name="bannerLinkType"
                          value="product"
                          checked={bannerLinkType === 'product'}
                          onChange={() => {
                            setBannerLinkType('product');
                            setRegulatoryLink(null);
                            setDeepLinkConfig({ linkType: 'product', productId: null });
                          }}
                          disabled={saving}
                        />
                        <span>Specific Product Link</span>
                      </label>

                      <label className="banner-link-option">
                        <input
                          type="radio"
                          name="bannerLinkType"
                          value="special"
                          checked={bannerLinkType === 'special'}
                          onChange={() => {
                            setBannerLinkType('special');
                            setRegulatoryLink(null);
                            setDeepLinkConfig({ linkType: 'special', specialId: undefined });
                          }}
                          disabled={saving}
                        />
                        <span>Dutchie Deal</span>
                      </label>
                    </>
                  )}

                  <label className="banner-link-option">
                    <input
                      type="radio"
                      name="bannerLinkType"
                      value="regulatory"
                      checked={bannerLinkType === 'regulatory'}
                      onChange={() => {
                        setBannerLinkType('regulatory');
                        setDeepLinkConfig(null);
                        setRegulatoryLink(null);
                      }}
                      disabled={saving}
                    />
                    <span>Regulatory Link (.gov only)</span>
                  </label>
                </div>

                {/* Show Deep Link Configurator when filter, product, or special is selected */}
                {(bannerLinkType === 'filter' || bannerLinkType === 'product' || bannerLinkType === 'special') && selectedDoc && getStoreContext(selectedDoc.id) && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <DeepLinkConfigurator
                      storeContext={getStoreContext(selectedDoc.id)!}
                      initialConfig={deepLinkConfig}
                      onChange={setDeepLinkConfig}
                    />
                  </div>
                )}

                {/* Show Regulatory Link Input when regulatory is selected */}
                {bannerLinkType === 'regulatory' && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <RegulatoryLinkInput
                      value={regulatoryLink}
                      onChange={setRegulatoryLink}
                      disabled={saving}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Deep Link Preview (view mode only) */}
            {viewingScheduledDeal && deepLinkConfig && (() => {
              const env = process.env.REACT_APP_ENV === 'qa' ? 'qa' : 'production' as Environment;
              const deepLinkUrl = generateDeepLinkUrl(deepLinkConfig, env);
              return (
                <div className="deal-preview">
                  <h4>Banner Link</h4>
                  <div style={{
                    padding: '1rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: '#f9f9f9',
                    marginTop: '0.5rem'
                  }}>
                    <p><strong>Link Type:</strong> {deepLinkConfig.linkType === 'filter' ? 'Filter' : 'Product'}</p>
                    {deepLinkConfig.categories && deepLinkConfig.categories.length > 0 && (
                      <p><strong>Categor{deepLinkConfig.categories.length > 1 ? 'ies' : 'y'}:</strong> {deepLinkConfig.categories.join(', ')}</p>
                    )}
                    {deepLinkConfig.brands && deepLinkConfig.brands.length > 0 && (
                      <p><strong>Brands:</strong> {deepLinkConfig.brands.join(', ')}</p>
                    )}
                    {deepLinkConfig.productId && <p><strong>Product ID:</strong> {deepLinkConfig.productId}</p>}
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #ddd' }}>
                      <p style={{ marginBottom: '0.5rem' }}><strong>Generated URL:</strong></p>
                      <a
                        href={deepLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          padding: '0.5rem 1rem',
                          background: '#007bff',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#0056b3'}
                        onMouseOut={(e) => e.currentTarget.style.background = '#007bff'}
                      >
                        🔗 Open Link in New Tab
                      </a>
                      <div style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem',
                        background: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        wordBreak: 'break-all',
                        fontFamily: 'monospace'
                      }}>
                        {deepLinkUrl}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Regulatory Link Preview (view mode only) */}
            {viewingScheduledDeal && regulatoryLink && (
              <div className="deal-preview">
                <h4>Regulatory Link</h4>
                <div style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: '#f9f9f9',
                  marginTop: '0.5rem'
                }}>
                  <p style={{ marginBottom: '0.75rem' }}>
                    <strong>Type:</strong> Government Regulatory Information (.gov)
                  </p>
                  <a
                    href={regulatoryLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '0.5rem 1rem',
                      background: '#00443B',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#006b56'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#00443B'}
                  >
                    🏛️ Open Regulatory Link in New Tab
                  </a>
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.5rem',
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace'
                  }}>
                    {regulatoryLink}
                  </div>
                </div>
              </div>
            )}

            {/* Scheduling controls for new and edited scheduled deals */}
            {schedulingEnabled && selectedDoc && (
              <div className="scheduling-section" data-testid="scheduling-section">
                <div className="scheduling-header">
                  <h4>{viewingScheduledDeal ? 'View Deal' : 'Schedule Deal'}</h4>
                  <div className="timezone-display">
                    Scheduling in {getTimezoneAbbreviation(selectedDoc.id)} (store local time)
                    {isBrowserTimzoneDifferent(selectedDoc.id) && (
                      <div className="timezone-warning">
                        ⚠️ Your browser timezone differs from store timezone. Times will be converted automatically.
                      </div>
                    )}
                  </div>
                </div>

                {/* Menu Type Selector - Publish to Med/Rec/Both */}
                {!viewingScheduledDeal && (() => {
                  const pairedIds = getPairedMenuIds(selectedDoc.id);
                  const hasBothMenus = !!(pairedIds.medical && pairedIds.recreational);

                  // Only show selector if store has both medical and recreational menus
                  if (!hasBothMenus) return null;

                  return (
                    <div className="menu-type-selector" data-testid="menu-type-selector">
                      <label>Publish to:</label>
                      <div className="menu-type-options">
                        <label className="menu-type-option">
                          <input
                            type="checkbox"
                            data-testid="menu-type-medical"
                            checked={targetMenuTypes.includes('medical')}
                            onChange={() => {
                              setTargetMenuTypes(prev =>
                                prev.includes('medical')
                                  ? prev.filter(t => t !== 'medical')
                                  : [...prev, 'medical']
                              );
                            }}
                            disabled={saving}
                          />
                          <span>Medical</span>
                        </label>
                        <label className="menu-type-option">
                          <input
                            type="checkbox"
                            data-testid="menu-type-recreational"
                            checked={targetMenuTypes.includes('recreational')}
                            onChange={() => {
                              setTargetMenuTypes(prev =>
                                prev.includes('recreational')
                                  ? prev.filter(t => t !== 'recreational')
                                  : [...prev, 'recreational']
                              );
                            }}
                            disabled={saving}
                          />
                          <span>Recreational</span>
                        </label>
                      </div>
                      {targetMenuTypes.length === 0 && (
                        <div className="menu-type-warning">
                          Please select at least one menu type
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="scheduling-inputs" data-testid="scheduling-inputs">
                  <div className="datetime-group">
                    <label>Start Date & Time:</label>
                    <div className="datetime-inputs">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="date-input"
                        min={new Date().toISOString().split('T')[0]} // Prevent past dates
                        disabled={!!viewingScheduledDeal && !isEditing}
                        data-testid="start-date"
                      />
                      <input
                        type="text"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="time-input"
                        placeholder="12:00"
                        pattern="^(0?[1-9]|1[0-2]):[0-5][0-9]$"
                        maxLength="5"
                        title="Enter time in 12-hour format (1:00 - 12:59)"
                        disabled={!!viewingScheduledDeal && !isEditing}
                        data-testid="start-time"
                      />
                      <select
                        value={startAmPm}
                        onChange={(e) => setStartAmPm(e.target.value as 'AM' | 'PM')}
                        className="ampm-select"
                        disabled={!!viewingScheduledDeal && !isEditing}
                        data-testid="start-ampm"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>
                  <div className="datetime-group">
                    <label>End Date & Time:</label>
                    <div className="datetime-inputs">
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="date-input"
                        min={startDate || new Date().toISOString().split('T')[0]} // Must be after start date
                        disabled={!!viewingScheduledDeal && !isEditing}
                        data-testid="end-date"
                      />
                      <input
                        type="text"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="time-input"
                        placeholder="12:00"
                        pattern="^(0?[1-9]|1[0-2]):[0-5][0-9]$"
                        maxLength="5"
                        title="Enter time in 12-hour format (1:00 - 12:59)"
                        disabled={!!viewingScheduledDeal && !isEditing}
                        data-testid="end-time"
                      />
                      <select
                        value={endAmPm}
                        onChange={(e) => setEndAmPm(e.target.value as 'AM' | 'PM')}
                        className="ampm-select"
                        disabled={!!viewingScheduledDeal && !isEditing}
                        data-testid="end-ampm"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Recurring Schedule Section - only for new deals */}
                {!viewingScheduledDeal && (
                  <div className="recurring-section" data-testid="recurring-section">
                    <div className="recurring-toggle">
                      <label className="recurring-checkbox-label">
                        <input
                          type="checkbox"
                          checked={recurringEnabled}
                          onChange={(e) => setRecurringEnabled(e.target.checked)}
                          data-testid="recurring-enabled-checkbox"
                        />
                        <span>Create recurring banners</span>
                      </label>
                    </div>

                    {recurringEnabled && (
                      <div className="recurring-options">
                        <div className="recurring-days">
                          <label>Repeat on:</label>
                          <div className="day-checkboxes">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                              <label key={day} className="day-checkbox">
                                <input
                                  type="checkbox"
                                  checked={recurringDays.includes(index)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setRecurringDays([...recurringDays, index].sort());
                                    } else {
                                      setRecurringDays(recurringDays.filter(d => d !== index));
                                    }
                                  }}
                                  data-testid={`recurring-day-${index}`}
                                />
                                <span>{day}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="recurring-weeks">
                          <label>For how many weeks:</label>
                          <select
                            value={recurringWeeks}
                            onChange={(e) => setRecurringWeeks(parseInt(e.target.value))}
                            className="weeks-select"
                            data-testid="recurring-weeks-select"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(w => (
                              <option key={w} value={w}>{w} week{w > 1 ? 's' : ''}</option>
                            ))}
                          </select>
                        </div>

                        {recurringDays.length > 0 && startTime && endTime && (
                          <div className="recurring-preview">
                            <strong>Will create {recurringDays.length * recurringWeeks} banners:</strong>
                            <div className="preview-dates">
                              {generateRecurringDates(startTime, startAmPm, endTime, endAmPm, recurringDays, recurringWeeks, selectedDoc?.id || '')
                                .slice(0, 5)
                                .map((d, i) => (
                                  <div key={i} className="preview-date">
                                    {d.startDateTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </div>
                                ))}
                              {recurringDays.length * recurringWeeks > 5 && (
                                <div className="preview-more">+{recurringDays.length * recurringWeeks - 5} more</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="editor-actions">
              <button
                onClick={() => {
                  // Clear viewing and editing state
                  setDealData(null);
                  setViewingScheduledDeal(null);
                  setIsEditing(false);
                  setSchedulingEnabled(false);
                  setStartDateTime(null);
                  setEndDateTime(null);
                  // Clear separate date/time inputs
                  setStartDate('');
                  setStartTime('');
                  setStartAmPm('AM');
                  setEndDate('');
                  setEndTime('');
                  setEndAmPm('PM');
                  // Clear image state
                  setMobileImageUrl(null);
                  setDesktopImageUrl(null);
                  setMobileImageFile(null);
                  setDesktopImageFile(null);
                  // Clear deep link
                  setDeepLinkConfig(null);
                  // Clear regulatory link
                  setRegulatoryLink(null);
                  // Clear banner link type
                  setBannerLinkType('none');
                  // Clear recurring state
                  setRecurringEnabled(false);
                  setRecurringDays([]);
                  setRecurringWeeks(4);
                }}
                disabled={saving}
                className="cancel-button"
                data-testid="cancel-deal-button"
              >
                Cancel
              </button>
              
              {/* Edit button for viewing scheduled deals */}
              {viewingScheduledDeal && !isEditing && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to edit this scheduled deal?')) {
                      setIsEditing(true);
                    }
                  }}
                  disabled={saving}
                  className="edit-button"
                  data-testid="edit-scheduled-deal-button"
                >
                  Edit Deal
                </button>
              )}

              {schedulingEnabled && (!viewingScheduledDeal || isEditing) ? (
                <button
                  onClick={async () => {
                    // Handle scheduling new deal or updating existing scheduled deal
                    if (!selectedDoc || !startDateTime || !endDateTime) {
                      alert('Please fill in all required fields including start and end times.');
                      return;
                    }

                    // Validate target menu types for new deals
                    const pairedIds = getPairedMenuIds(selectedDoc.id);
                    const hasBothMenus = !!(pairedIds.medical && pairedIds.recreational);
                    if (!viewingScheduledDeal && hasBothMenus && targetMenuTypes.length === 0) {
                      alert('Please select at least one menu type to publish to.');
                      return;
                    }

                    setSaving(true);
                    try {
                      // Handle image deals (always image type)
                      if (viewingScheduledDeal) {
                          // Update existing scheduled image deal
                          // Only require files if no existing images, or only pass files if they were uploaded
                          const updates: any = {
                            startDateTime: startDateTime!,
                            endDateTime: endDateTime!,
                            deepLink: deepLinkConfig,
                            regulatoryLink: regulatoryLink
                          };

                          if (mobileImageFile) updates.mobileImage = mobileImageFile;
                          if (desktopImageFile) updates.desktopImage = desktopImageFile;

                          await updateScheduledImageDeal(selectedDoc.id, viewingScheduledDeal.documentId, updates);
                          alert('Scheduled image deal updated successfully!');
                          setIsEditing(false);
                          setViewingScheduledDeal(null);
                        } else {
                          // Create new scheduled image deal - requires both images
                          if (!mobileImageFile || !desktopImageFile) {
                            alert('Please upload both mobile and desktop images.');
                            return;
                          }

                          // Determine which menu IDs to create deals for
                          const menuIdsToCreate: string[] = [];
                          if (hasBothMenus) {
                            if (targetMenuTypes.includes('medical') && pairedIds.medical) {
                              menuIdsToCreate.push(pairedIds.medical);
                            }
                            if (targetMenuTypes.includes('recreational') && pairedIds.recreational) {
                              menuIdsToCreate.push(pairedIds.recreational);
                            }
                          } else {
                            // Store only has one menu type, use current selected doc
                            menuIdsToCreate.push(selectedDoc.id);
                          }

                          // Handle recurring deals
                          if (recurringEnabled && recurringDays.length > 0) {
                            const recurringDates = generateRecurringDates(
                              startTime, startAmPm, endTime, endAmPm,
                              recurringDays, recurringWeeks, selectedDoc.id
                            );

                            if (recurringDates.length === 0) {
                              alert('No valid dates generated for recurring schedule.');
                              return;
                            }

                            // Confirm before creating multiple deals
                            const totalDeals = recurringDates.length * menuIdsToCreate.length;
                            const confirmed = window.confirm(
                              `This will create ${totalDeals} scheduled deal${totalDeals > 1 ? 's' : ''}. Continue?`
                            );
                            if (!confirmed) {
                              setSaving(false);
                              return;
                            }

                            // Create deals for each date and menu type
                            let createdCount = 0;
                            for (const { startDateTime: recStart, endDateTime: recEnd } of recurringDates) {
                              for (const menuId of menuIdsToCreate) {
                                await createScheduledImageDeal(
                                  menuId,
                                  mobileImageFile,
                                  desktopImageFile,
                                  recStart,
                                  recEnd,
                                  deepLinkConfig,
                                  regulatoryLink
                                );
                                createdCount++;
                              }
                            }

                            alert(`Successfully created ${createdCount} recurring scheduled deals!`);
                            // Reset recurring state
                            setRecurringEnabled(false);
                            setRecurringDays([]);
                            setRecurringWeeks(4);
                          } else {
                            // Single deal creation (existing logic)
                            for (const menuId of menuIdsToCreate) {
                              await createScheduledImageDeal(
                                menuId,
                                mobileImageFile,
                                desktopImageFile,
                                startDateTime!,
                                endDateTime!,
                                deepLinkConfig,
                                regulatoryLink
                              );
                            }

                            const menuTypesStr = menuIdsToCreate.length > 1 ? 'both Medical and Recreational' : 'the selected menu';
                            alert(`Scheduled image deal created successfully for ${menuTypesStr}!`);
                          }
                        }

                      // Refresh data
                      const updatedData = await getAllStoreDeals(selectedDoc.id);
                      setStoreDealsData(updatedData);
                      
                      // Clear form state
                      setDealData(null);
                      setViewingScheduledDeal(null);
                      setSchedulingEnabled(false);
                      setStartDateTime(null);
                      setEndDateTime(null);
                      // Clear separate date/time inputs
                      setStartDate('');
                      setStartTime('');
                      setStartAmPm('AM');
                      setEndDate('');
                      setEndTime('');
                      setEndAmPm('PM');
                      // Clear image state
                      setMobileImageUrl(null);
                      setDesktopImageUrl(null);
                      setMobileImageFile(null);
                      setDesktopImageFile(null);
                      // Clear deep link
                      setDeepLinkConfig(null);
                      // Clear regulatory link
                      setRegulatoryLink(null);
                      // Clear banner link type
                      setBannerLinkType('none');

                    } catch (error) {
                      console.error('Error saving scheduled deal:', error);
                      alert(`Failed to save scheduled deal: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    } finally {
                      setSaving(false);
                    }
                  }} 
                  disabled={saving || !startDateTime || !endDateTime || !((mobileImageFile && desktopImageFile) || (mobileImageUrl && desktopImageUrl))}
                  className="publish-button"
                  data-testid={isEditing ? 'update-deal-button' : 'schedule-deal-button'}
                >
                  {saving ? (isEditing ? 'Updating...' : 'Scheduling...') : (isEditing ? 'Update Deal' : 'Schedule Deal')}
                </button>
              ) : dealData?.isPublished ? (
                <button 
                  onClick={() => handleSave(true)} 
                  disabled={saving}
                  className="save-button"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              ) : (
                <>
                  <button 
                    onClick={() => handleSave(false)} 
                    disabled={saving}
                    className="save-button"
                  >
                    {saving ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button 
                    onClick={() => handleSave(true)} 
                    disabled={saving}
                    className="publish-button"
                  >
                    {saving ? 'Publishing...' : 'Save & Publish'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    );
  }

  if (mode === 'cart-message') {
    return (
      <div className="admin-portal-container" data-testid="admin-portal">
        <Helmet>
          <title>Cart Message - Admin Portal - Private</title>
          <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
          <meta name="googlebot" content="noindex, nofollow" />
          <meta name="description" content="Private admin area - not for public access" />
        </Helmet>
        <div className="admin-portal-header">
          <div className="header-left">
            <h1>Cart Message Configuration</h1>
          </div>
          <div className="user-info">
            <span>Logged in as: {user?.email || (isTestMode ? 'Test User' : '')}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
        <CartMessageEditor onBack={() => setMode('selector')} />
      </div>
    );
  }

  if (mode === 'home-collections' && homeCollectionsContext) {
    return (
      <div className="admin-portal-container" data-testid="admin-portal">
        <Helmet>
          <title>Home Collections - {homeCollectionsContext.displayName} - Private</title>
          <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
          <meta name="googlebot" content="noindex, nofollow" />
          <meta name="description" content="Private admin area - not for public access" />
        </Helmet>
        <div className="admin-portal-header">
          <div className="header-left">
            <button onClick={() => setMode('selector')} className="back-button">
              &larr; Back to Admin
            </button>
          </div>
          <div className="user-info">
            <span>Logged in as: {user?.email || (isTestMode ? 'Test User' : '')}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
        <HomeCollectionsEditor
          storeName={homeCollectionsContext.storeName}
          menuType={homeCollectionsContext.menuType as 'med' | 'rec'}
          displayName={homeCollectionsContext.displayName}
          retailerId={homeCollectionsContext.retailerId}
          legacyMenuId={homeCollectionsContext.legacyMenuId}
          onBack={() => setMode('selector')}
        />
      </div>
    );
  }

  if (mode === 'menu-audit' && selectedMenu && menuAuditProducts) {
    return (
      <div className="admin-portal-container" data-testid="admin-portal">
        <Helmet>
          <title>Menu Audit - {selectedMenu.storeName} - Private</title>
          <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
          <meta name="googlebot" content="noindex, nofollow" />
          <meta name="description" content="Private admin area - not for public access" />
        </Helmet>
        <div className="admin-portal-header">
          <div className="header-left">
            <button onClick={() => setMode('selector')} className="back-button">
              ← Back to Admin
            </button>
          </div>
          <div className="user-info">
            <span>Logged in as: {user?.email || (isTestMode ? 'Test User' : '')}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
        <MenuAuditTable
          products={menuAuditProducts}
          storeName={selectedMenu.storeName}
          menuType={selectedMenu.menuType}
        />
      </div>
    );
  }

  // Fallback
  return null;
};

export default AdminPortal;